import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Heart } from "lucide-react";

const KEY = "mp_quote_date";

export function DailyQuote() {
  const { t, lang } = useI18n();
  const [quote, setQuote] = useState("");
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(KEY) === today) return;
    api.get(`/daily-quote?lang=${lang}`).then(({ data }) => {
      if (data?.quote) {
        setQuote(data.quote);
        setOpen(true);
      }
    }).catch(() => {});
  }, [lang]);

  const dismiss = () => {
    localStorage.setItem(KEY, new Date().toISOString().slice(0, 10));
    setOpen(false);
  };

  const save = async () => {
    try {
      await api.post("/favorites", { text: quote, source: "ai" });
      setSaved(true);
      toast.success(t("quote.saved"));
    } catch {
      toast.error("Couldn't save that just now.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="max-w-md rounded-3xl border-border/60 text-center" data-testid="daily-quote-dialog">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col items-center px-2 py-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <p className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">{t("quote.title")}</p>
          <p className="mt-3 font-serif text-2xl leading-snug" data-testid="daily-quote-text">“{quote}”</p>
          <div className="mt-7 flex items-center gap-2">
            <Button variant="outline" onClick={save} disabled={saved} className="rounded-full gap-2" data-testid="daily-quote-save">
              <Heart className={`h-4 w-4 ${saved ? "fill-current" : ""}`} strokeWidth={1.5} /> {saved ? t("quote.saved") : t("quote.save")}
            </Button>
            <Button onClick={dismiss} className="rounded-full px-6" data-testid="daily-quote-dismiss">
              {t("quote.close")}
            </Button>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
